import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Editor from '@monaco-editor/react';
import { toolApi, integrationApi, type ToolDefinition, type Integration } from '@/lib/api';
import { Wrench, Code2, Globe2, Plus, Trash2, Play, ChevronRight, Check, AlertCircle, Info, KeyRound } from 'lucide-react';
import styles from './_auth.settings.tools.module.css';
import { PageGuide } from '@/components/PageGuide';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';


export const Route = createFileRoute('/_auth/settings/tools')({
  component: ToolsPage,
});

type ExecutorType = 'HTTP' | 'CODE';

const DEFAULT_CODE = `// Tool function — receives 'params' object, must return a value
// Available: params (object with input fields)
// No network access. 5 second timeout.

function execute(params) {
  // Example: format a currency amount
  const { amount, currency = 'EUR' } = params;
  return {
    formatted: new Intl.NumberFormat('fi-FI', { style: 'currency', currency }).format(amount),
    original: amount,
  };
}

return execute(params);
`;

const DEFAULT_INPUT_SCHEMA = JSON.stringify({
  type: 'object',
  properties: {
    example_param: { type: 'string', description: 'Example parameter' },
  },
  required: [],
}, null, 2);

const EMPTY_TOOL = {
  toolName: '',
  displayName: '',
  description: '',
  executorType: 'HTTP' as ExecutorType,
  integrationId: '',
  httpMethod: 'GET',
  httpPath: '',
  sandboxCode: DEFAULT_CODE,
  inputSchema: DEFAULT_INPUT_SCHEMA,
  outputSchema: '{}',
  contextMapping: [] as Array<{ key: string; inject: 'path' | 'query' | 'body' | 'header'; header?: string }>,
};

function toToolName(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/__+/g, '_');
}

export default function ToolsPage() {
  const qc = useQueryClient();
  const { data: tools = [], isLoading } = useQuery({ queryKey: ['tools'], queryFn: toolApi.list });
  const { data: integrations = [] } = useQuery({ queryKey: ['integrations'], queryFn: integrationApi.list });

  const [selected, setSelected] = useState<ToolDefinition | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [form, setForm] = useState(EMPTY_TOOL);
  const [testInput, setTestInput] = useState('{}');
  const [testResult, setTestResult] = useState<{ result?: unknown; error?: string; duration?: number } | null>(null);
  const [testing, setTesting] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const set = (k: string, v: unknown) => setForm(f => ({ ...f, [k]: v }));

  const createMutation = useMutation({
    mutationFn: () => toolApi.create({
      toolName: form.toolName,
      displayName: form.displayName,
      description: form.description,
      executorType: form.executorType,
      integrationId: form.integrationId || undefined,
      httpConfig: form.executorType === 'HTTP' ? { method: form.httpMethod as 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: form.httpPath } : undefined,
      sandboxCode: form.executorType === 'CODE' ? form.sandboxCode : undefined,
      inputSchema: JSON.parse(form.inputSchema || '{}'),
      outputSchema: JSON.parse(form.outputSchema || '{}'),
      contextMapping: form.contextMapping.length > 0
        ? Object.fromEntries(form.contextMapping.map(r => [r.key, { inject: r.inject, ...(r.header ? { header: r.header } : {}) }]))
        : undefined,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tools'] }); setIsNew(false); },
  });

  const updateMutation = useMutation({
    mutationFn: (id: string) => toolApi.update(id, {
      displayName: form.displayName,
      description: form.description,
      integrationId: form.integrationId || undefined,
      httpConfig: form.executorType === 'HTTP' ? { method: form.httpMethod as 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: form.httpPath } : undefined,
      sandboxCode: form.executorType === 'CODE' ? form.sandboxCode : undefined,
      inputSchema: JSON.parse(form.inputSchema || '{}'),
      outputSchema: JSON.parse(form.outputSchema || '{}'),
      contextMapping: form.contextMapping.length > 0
        ? Object.fromEntries(form.contextMapping.map(r => [r.key, { inject: r.inject, ...(r.header ? { header: r.header } : {}) }]))
        : null,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tools'] }); },
  });

  const deleteMutation = useMutation({
    mutationFn: toolApi.delete,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tools'] }); setSelected(null); },
  });

  const handleTest = async (toolId: string) => {
    setTesting(true);
    setTestResult(null);
    try {
      const params = JSON.parse(testInput);
      const start = Date.now();
      const result = await toolApi.test(toolId, params);
      setTestResult({ result, duration: Date.now() - start });
    } catch (e: unknown) {
      setTestResult({ error: e instanceof Error ? e.message : String(e) });
    } finally {
      setTesting(false);
    }
  };

  const openEdit = (tool: ToolDefinition) => {
    setIsNew(false);
    setSelected(tool);
    setTestResult(null);
    // Parse existing contextMapping from object → array form
    const existingMapping = tool.contextMapping as Record<string, { inject: string; header?: string }> | null;
    const mappingArr = existingMapping
      ? Object.entries(existingMapping).map(([key, rule]) => ({
          key,
          inject: rule.inject as 'path' | 'query' | 'body' | 'header',
          header: rule.header,
        }))
      : [];
    setForm({
      toolName: tool.toolName,
      displayName: tool.displayName,
      description: tool.description,
      executorType: tool.executorType === 'CODE' ? 'CODE' : 'HTTP',
      integrationId: tool.integrationId ?? '',
      httpMethod: (tool.httpConfig as { method?: string })?.method ?? 'GET',
      httpPath: (tool.httpConfig as { path?: string })?.path ?? '',
      sandboxCode: tool.sandboxCode ?? DEFAULT_CODE,
      inputSchema: tool.inputSchema ? JSON.stringify(tool.inputSchema, null, 2) : DEFAULT_INPUT_SCHEMA,
      outputSchema: tool.outputSchema ? JSON.stringify(tool.outputSchema, null, 2) : '{}',
      contextMapping: mappingArr,
    });
  };

  const openNew = () => {
    setSelected(null);
    setIsNew(true);
    setForm(EMPTY_TOOL);
    setTestResult(null);
  };

  const httpTools = (tools as ToolDefinition[]).filter(t => t.executorType === 'HTTP');
  const codeTools = (tools as ToolDefinition[]).filter(t => t.executorType === 'CODE');

  const editorPanel = selected || isNew;

  return (
    <>
      <div style={{ padding: '16px 24px 0' }}>
        <PageGuide pageKey="tools" />
      </div>
      <div className={styles.layout}>
      {/* ── Left panel: tool list ── */}
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <h2 className={styles.sidebarTitle}>Tools</h2>
          <button className={styles.newBtn} onClick={openNew}><Plus size={15} /></button>
        </div>

        {isLoading ? (
          <div className={styles.sidebarEmpty}>Loading…</div>
        ) : (
          <>
            <div className={styles.sidebarSection}>
              <div className={styles.sectionLabel}><Globe2 size={13} /> HTTP Tools</div>
              {httpTools.length === 0 ? (
                <div className={styles.sidebarEmpty}>None yet</div>
              ) : httpTools.map(t => (
                <button
                  key={t.id}
                  className={`${styles.toolRow} ${selected?.id === t.id ? styles.active : ''}`}
                  onClick={() => openEdit(t)}
                >
                  <span className={styles.toolName}>{t.displayName}</span>
                  <ChevronRight size={14} className={styles.chevron} />
                </button>
              ))}
            </div>

            <div className={styles.sidebarSection}>
              <div className={styles.sectionLabel}><Code2 size={13} /> Code Tools</div>
              {codeTools.length === 0 ? (
                <div className={styles.sidebarEmpty}>None yet</div>
              ) : codeTools.map(t => (
                <button
                  key={t.id}
                  className={`${styles.toolRow} ${selected?.id === t.id ? styles.active : ''}`}
                  onClick={() => openEdit(t)}
                >
                  <span className={styles.toolName}>{t.displayName}</span>
                  <ChevronRight size={14} className={styles.chevron} />
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Right panel: editor ── */}
      {!editorPanel ? (
        <div className={styles.emptyEditor}>
          <Wrench size={40} className={styles.emptyIcon} />
          <h3>Select a tool to edit</h3>
          <p>Or create a new HTTP or Code tool</p>
          <button className={styles.primaryBtn} onClick={openNew}><Plus size={16} /> New Tool</button>
        </div>
      ) : (
        <div className={styles.editor}>
          <div className={styles.editorHeader}>
            <div className={styles.editorTitle}>
              {isNew ? 'New Tool' : form.displayName}
              {!isNew && (
                <span className={`${styles.typePill} ${form.executorType === 'CODE' ? styles.codeType : styles.httpType}`}>
                  {form.executorType === 'CODE' ? <Code2 size={12} /> : <Globe2 size={12} />}
                  {form.executorType}
                </span>
              )}
            </div>
            <div className={styles.editorActions}>
              {!isNew && (
                <button className={styles.deleteBtn} onClick={() => setDeleteConfirmId(selected!.id)}>
                  <Trash2 size={14} /> Delete
                </button>
              )}
              <button
                className={styles.saveBtn}
                onClick={() => isNew ? createMutation.mutate() : updateMutation.mutate(selected!.id)}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                <Check size={14} /> {isNew ? 'Create' : 'Save'}
              </button>
            </div>
          </div>

          <div className={styles.editorBody}>
            {/* ── Basic info ── */}
            <section className={styles.section}>
              <h4 className={styles.sectionTitle}>Basic info</h4>
              <div className={styles.fieldGrid}>
                <label>
                  <span>Display name</span>
                  <input value={form.displayName}
                    onChange={e => { set('displayName', e.target.value); if (isNew) set('toolName', toToolName(e.target.value)); }}
                    placeholder="Order Status Lookup" />
                </label>
                <label>
                  <span>Tool name (used by LLM)</span>
                  <input value={form.toolName}
                    onChange={e => set('toolName', toToolName(e.target.value))}
                    placeholder="order_status_lookup" className={styles.mono} />
                </label>
                <label className={styles.fullWidth}>
                  <span>Description <em>(shown to the LLM — be precise)</em></span>
                  <textarea value={form.description} onChange={e => set('description', e.target.value)}
                    placeholder="Look up the status of a customer order by order ID. Returns status, estimated delivery date, and tracking link." rows={3} />
                </label>
                {isNew && (
                  <label>
                    <span>Executor type</span>
                    <select value={form.executorType} onChange={e => set('executorType', e.target.value as ExecutorType)}>
                      <option value="HTTP">HTTP — call external REST API</option>
                      <option value="CODE">Code — sandboxed JavaScript</option>
                    </select>
                  </label>
                )}
              </div>
            </section>

            {/* ── HTTP config ── */}
            {form.executorType === 'HTTP' && (
              <section className={styles.section}>
                <h4 className={styles.sectionTitle}>HTTP Configuration</h4>
                <div className={styles.fieldGrid}>
                  <label>
                    <span>Integration (base URL + auth)</span>
                    <select value={form.integrationId} onChange={e => set('integrationId', e.target.value)}>
                      <option value="">— select integration —</option>
                      {(integrations as Integration[]).map(i => (
                        <option key={i.id} value={i.id}>{i.name} ({i.config?.baseUrl})</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>HTTP Method</span>
                    <select value={form.httpMethod} onChange={e => set('httpMethod', e.target.value)}>
                      {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(m => <option key={m}>{m}</option>)}
                    </select>
                  </label>
                  <label className={styles.fullWidth}>
                    <span>Path <em>(use {'{param}'} for template variables)</em></span>
                    <input value={form.httpPath} onChange={e => set('httpPath', e.target.value)}
                      placeholder="/orders/{orderId}" className={styles.mono} />
                  </label>
                </div>
              </section>
            )}

            {/* ── Code editor ── */}
            {form.executorType === 'CODE' && (
              <section className={styles.section}>
                <h4 className={styles.sectionTitle}>
                  <Code2 size={15} /> JavaScript Code
                  <span className={styles.sandboxNote}>Isolated VM · No network · 5s timeout</span>
                </h4>
                <div className={styles.monacoWrap}>
                  <Editor
                    height="320px"
                    defaultLanguage="javascript"
                    value={form.sandboxCode}
                    onChange={v => set('sandboxCode', v ?? '')}
                    theme="vs-dark"
                    options={{
                      minimap: { enabled: false },
                      fontSize: 13,
                      lineNumbers: 'on',
                      scrollBeyondLastLine: false,
                      wordWrap: 'on',
                      padding: { top: 12 },
                    }}
                  />
                </div>
              </section>
            )}

            {/* ── Input schema ── */}
            <section className={styles.section}>
              <h4 className={styles.sectionTitle}>
                Input Schema <span className={styles.schemaNote}>JSON Schema — defines parameters the LLM can pass</span>
              </h4>
              <div className={styles.monacoWrap}>
                <Editor
                  height="180px"
                  defaultLanguage="json"
                  value={form.inputSchema}
                  onChange={v => set('inputSchema', v ?? '{}')}
                  theme="vs-dark"
                  options={{ minimap: { enabled: false }, fontSize: 12, lineNumbers: 'off', padding: { top: 8 } }}
                />
              </div>
            </section>

            {/* ── Session Context Mapping ── */}
            <section className={styles.section}>
              <h4 className={styles.sectionTitle}>
                <KeyRound size={14} /> Session Context Mapping
                <span className={styles.schemaNote}>Inject trusted user identity into this tool call</span>
              </h4>
              {/* Explainer */}
              <div style={{
                padding: '10px 14px', borderRadius: 8, marginBottom: 12,
                background: '#6366f110', border: '1px solid #6366f130',
                fontSize: 12, color: '#a5b4fc', display: 'flex', gap: 8, alignItems: 'flex-start',
              }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>
                  When a conversation starts via API, the calling app passes a trusted <code style={{ background: '#6366f120', padding: '0 4px', borderRadius: 3 }}>sessionContext</code> (e.g. userId, accountId).
                  Map context keys here to inject them into this tool call — either as path params, query params, request body fields, or custom headers.
                  Leave empty to merge all context keys into params automatically.
                </span>
              </div>

              {/* Mapping rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {form.contextMapping.map((rule, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {/* Context key */}
                    <input
                      style={{ flex: '0 0 160px', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--color-text)', fontSize: 12, fontFamily: 'monospace' }}
                      placeholder="userId"
                      value={rule.key}
                      onChange={e => {
                        const next = [...form.contextMapping];
                        next[i] = { ...next[i], key: e.target.value };
                        set('contextMapping', next);
                      }}
                    />
                    <span style={{ color: 'var(--color-muted)', fontSize: 12 }}>→</span>
                    {/* Inject type */}
                    <select
                      style={{ flex: '0 0 110px', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--color-text)', fontSize: 12 }}
                      value={rule.inject}
                      onChange={e => {
                        const next = [...form.contextMapping];
                        next[i] = { ...next[i], inject: e.target.value as typeof rule.inject };
                        set('contextMapping', next);
                      }}
                    >
                      <option value="path">path param</option>
                      <option value="query">query param</option>
                      <option value="body">body field</option>
                      <option value="header">header</option>
                    </select>
                    {/* Header name (only when inject=header) */}
                    {rule.inject === 'header' && (
                      <input
                        style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--color-text)', fontSize: 12, fontFamily: 'monospace' }}
                        placeholder="X-User-Id"
                        value={rule.header ?? ''}
                        onChange={e => {
                          const next = [...form.contextMapping];
                          next[i] = { ...next[i], header: e.target.value };
                          set('contextMapping', next);
                        }}
                      />
                    )}
                    {rule.inject !== 'header' && (
                      <span style={{ flex: 1, fontSize: 11, color: 'var(--color-muted)', fontFamily: 'monospace', padding: '0 4px' }}>
                        {rule.inject === 'path' && form.httpPath ? `{${rule.key || '?'}} in path` : rule.inject === 'query' ? `?${rule.key || '?'}=…` : rule.inject === 'body' ? `{ ${rule.key || '?'}: … }` : ''}
                      </span>
                    )}
                    <button
                      onClick={() => set('contextMapping', form.contextMapping.filter((_, j) => j !== i))}
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}

                <button
                  onClick={() => set('contextMapping', [...form.contextMapping, { key: '', inject: 'path' as const }])}
                  style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 6, border: '1px dashed var(--border)', background: 'transparent', color: 'var(--color-muted)', cursor: 'pointer', fontSize: 12 }}
                >
                  <Plus size={12} /> Add context key
                </button>
              </div>
            </section>

            {/* ── Test runner ── */}
            {!isNew && selected && (
              <section className={styles.section}>
                <h4 className={styles.sectionTitle}><Play size={14} /> Test Runner</h4>
                <div className={styles.testGrid}>
                  <div>
                    <p className={styles.testLabel}>Input (JSON)</p>
                    <div className={styles.monacoWrap}>
                      <Editor
                        height="120px"
                        defaultLanguage="json"
                        value={testInput}
                        onChange={v => setTestInput(v ?? '{}')}
                        theme="vs-dark"
                        options={{ minimap: { enabled: false }, fontSize: 12, lineNumbers: 'off', padding: { top: 8 } }}
                      />
                    </div>
                  </div>
                  <div>
                    <p className={styles.testLabel}>Result</p>
                    <div className={`${styles.testResult} ${testResult?.error ? styles.testError : ''}`}>
                      {testResult ? (
                        testResult.error ? (
                          <><AlertCircle size={14} /> {testResult.error}</>
                        ) : (
                          <><pre>{JSON.stringify(testResult.result, null, 2)}</pre><div className={styles.duration}>{testResult.duration}ms</div></>
                        )
                      ) : <span className={styles.testPlaceholder}>Run the tool to see results</span>}
                    </div>
                  </div>
                </div>
                <button className={styles.runBtn} onClick={() => handleTest(selected.id)} disabled={testing}>
                  <Play size={14} /> {testing ? 'Running…' : 'Run Tool'}
                </button>
              </section>
            )}
          </div>
        </div>
      )}
    </div>
    <ConfirmDialog
      open={!!deleteConfirmId}
      onCancel={() => setDeleteConfirmId(null)}
      onConfirm={() => { if (deleteConfirmId) { deleteMutation.mutate(deleteConfirmId); setDeleteConfirmId(null); } }}
      title="Delete this tool?"
      description="This will permanently remove the tool definition. Any skills or agents referencing it may break."
      confirmLabel="Delete Tool"
      variant="danger"
    />
    </>
  );
}
